import { useEffect } from 'react';
import {
    WM_MSG,
    WM_HEARTBEAT_INTERVAL,
    bumpWindowOpenedAt,
    createWindowManagerChannel,
    getChildWindowMeta,
    getOrCreateWindowId,
    getOrCreateWindowOpenedAt,
    installWindowOpenTracker,
    isChildPopup
} from 'services/window-manager.service';

const WindowRegistryClient = () => {
    useEffect(() => {
        // Children spawn windows too; patch their window.open so grandchildren
        // don't keep the inherited wmWindowId (no-op where already installed).
        installWindowOpenTracker();
        if (!isChildPopup()) return undefined;
        const channel = createWindowManagerChannel();
        if (!channel) return undefined;

        const id = getOrCreateWindowId();
        let openedAt = getOrCreateWindowOpenedAt();
        const post = (msg) => {
            try { channel.postMessage(msg); } catch (e) {
                console.log('post message error-->', e);
            }
        };
        const title = () => document.title || window.name || 'Window';
        const buildRegister = () => {
            const meta = getChildWindowMeta() || {};
            return {
                type: WM_MSG.REGISTER,
                id,
                name: window.name || '',
                title: title(),
                url: window.location.href,
                openedAt,
                command: meta.command || '',
                line2: meta.line2 || ''
            };
        };
        post(buildRegister());

        const handleFocus = () => {
            openedAt = bumpWindowOpenedAt();
            post(buildRegister());
        };
        if (document.hasFocus()) handleFocus();

        channel.onmessage = (event) => {
            const data = event.data || {};
            switch (data.type) {
                case WM_MSG.REQUEST_ROSTER:
                    post(buildRegister());
                    break;
                case WM_MSG.CLOSE_ALL:
                    window.close();
                    break;
                case WM_MSG.CLOSE:
                    if (data.id === id) window.close();
                    break;
                case WM_MSG.FOCUS:
                    if (data.id === id) window.focus();
                    break;
                case WM_MSG.FOCUS_ALL:
                    window.focus();
                    break;
                default:
                    break;
            }
        };

        const sendHeartbeat = () => {
            const meta = getChildWindowMeta() || {};
            post({
                type: WM_MSG.HEARTBEAT, id, name: window.name || '', ts: Date.now(), title: title(), url: window.location.href, command: meta.command || '', line2: meta.line2 || ''
            });
        };
        const heartbeat = setInterval(sendHeartbeat, WM_HEARTBEAT_INTERVAL);

        const handleUnload = () => post({ type: WM_MSG.UNREGISTER, id });
        // Background tabs throttle the heartbeat timer; re-announce the instant
        // the popup becomes visible so the parent's roster doesn't flicker.
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') sendHeartbeat();
        };

        window.addEventListener('pagehide', handleUnload);
        window.addEventListener('beforeunload', handleUnload);
        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            clearInterval(heartbeat);
            window.removeEventListener('pagehide', handleUnload);
            window.removeEventListener('beforeunload', handleUnload);
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibility);
            post({ type: WM_MSG.UNREGISTER, id });
            try { channel.close(); } catch (e) {
                console.log('channel close error-->', e);
            }
        };
    }, []);

    return null;
};

export default WindowRegistryClient;
